import { useEffect, useState } from 'react';
import { ApiError, type Api, type CatalogTemplate } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';
import { CreateHero } from './CreateHero';

/** Spoken languages the platform ships templates in (BCP 47; a new language = a new locale in the files + here). */
export const AGENT_LANGUAGES = ['tr-TR', 'en-US', 'ar-SA'];

type Preview = Awaited<ReturnType<Api['templatePreview']>>;
const nameOr = (t: T, key: string, fallback: string) => (t(key) === key ? fallback : t(key));
const list = (v: unknown): Array<Record<string, unknown>> => (Array.isArray(v) ? (v as Array<Record<string, unknown>>) : []);

/**
 * ADR-038 ready agents (separate from the studio interview): pick a template by spoken language, industry and
 * direction; see what the agent will do, filled with the business's own details; create it in one step.
 */
export const ReadyAgentsPage = ({ api, t, onCreated, onStudio, onStudioSession }: { api: Api; t: T; onCreated: (agentId: string) => void; onStudio: () => void; onStudioSession: (sessionId: string) => void }) => {
  const panelLang = document.documentElement.lang || navigator.language || 'en';
  // ADR-071: creating an agent, the AI finder and the studio need centeros.agents (App also gates this page);
  // browsing the catalogue and the preview are for everyone who may see.
  const canCreate = useCan()(PERM.agents);
  const [agentLanguage, setAgentLanguage] = useState(() => AGENT_LANGUAGES.find((l) => l.startsWith(panelLang.split('-')[0]!)) ?? AGENT_LANGUAGES[0]!);
  const [sector, setSector] = useState('');
  const [direction, setDirection] = useState('');
  const [templates, setTemplates] = useState<CatalogTemplate[]>();
  const [picked, setPicked] = useState<CatalogTemplate>();
  const [preview, setPreview] = useState<Preview>();
  const [name, setName] = useState('');
  const [contactSource, setContactSource] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    setTemplates(undefined);
    setPicked(undefined);
    api.templates({ lang: agentLanguage }).then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, [api, agentLanguage]);

  useEffect(() => {
    setPreview(undefined);
    setError(undefined);
    setValues({});
    setContactSource('');
    if (!picked) return;
    api
      .templatePreview(picked.id, agentLanguage)
      .then((p) => {
        setPreview(p);
        setName(String(p.seed['identity.name'] ?? ''));
      })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api, picked, agentLanguage]);

  const sectors = [...new Set((templates ?? []).map((x) => x.sector))];
  const shown = (templates ?? []).filter((x) => (!sector || x.sector === sector) && (!direction || x.direction === direction || x.direction === 'both'));
  const needsSource = preview?.missing.includes('statements[contact_source].text') ?? false;
  const otherMissing = (preview?.missing ?? []).filter((m) => m !== 'statements[contact_source].text' && m !== 'identity.name');

  const create = async () => {
    if (!picked) return;
    setBusy(true);
    setError(undefined);
    try {
      const filled = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim()));
      const r = await api.createReadyAgent(picked.id, { agentLanguage, ...(name.trim() ? { name: name.trim() } : {}), ...(contactSource.trim() ? { contactSource: contactSource.trim() } : {}), ...(Object.keys(filled).length ? { values: filled } : {}) });
      // The sales kit starts building right away; the agent page shows its progress.
      await api.buildKits(r.agentId).catch(() => undefined);
      onCreated(r.agentId);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  const seed = preview?.seed ?? {};
  const [finder, setFinder] = useState(false);
  return (
    <section>
      <CreateHero title={t('create.hero.title')} body={t('ready.hero.body')}>
        {canCreate && (
          <>
            <button type="button" className="hero-primary" onClick={() => setFinder(true)}>✦ {t('ready.finder.open')}</button>
            <button type="button" className="hero-secondary" onClick={onStudio}>{t('ready.toStudio')}</button>
          </>
        )}
      </CreateHero>
      {finder && canCreate && (
        <Finder
          api={api}
          t={t}
          agentLanguage={agentLanguage}
          panelLanguage={panelLang}
          onPick={(x) => {
            setPicked(x);
            setFinder(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onCustom={onStudioSession}
          onClose={() => setFinder(false)}
        />
      )}
      <div className="template-filters">
        <label>{t('templates.agentLanguage')}
          <select value={agentLanguage} onChange={(e) => setAgentLanguage(e.target.value)}>
            {AGENT_LANGUAGES.map((l) => <option key={l} value={l}>{t(`templates.language.${l}`)}</option>)}
          </select>
        </label>
        <label>{t('templates.sector')}
          <select value={sector} onChange={(e) => setSector(e.target.value)}>
            <option value="">{t('templates.all')}</option>
            {sectors.map((x) => <option key={x} value={x}>{nameOr(t, `sector.${x}`, x)}</option>)}
          </select>
        </label>
        <label>{t('templates.direction')}
          <select value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="">{t('templates.all')}</option>
            <option value="inbound">{t('direction.inbound')}</option>
            <option value="outbound">{t('direction.outbound')}</option>
          </select>
        </label>
      </div>

      <div className={picked ? 'split ready-split' : ''}>
        <div className="template-grid">
          {shown.map((x) => (
            <button key={x.id} type="button" className={`template-card ${picked?.id === x.id ? 'active' : ''}`} onClick={() => setPicked(x)}>
              <span className="template-icon" aria-hidden>{x.icon}</span>
              <strong>{x.title}</strong>
              <span className="hint">{x.description}</span>
              <span className="template-tags">
                <span className="tag">{t(`direction.${x.direction}`)}</span>
                <span className="tag">{nameOr(t, `sector.${x.sector}`, x.sector)}</span>
                <span className="tag">{nameOr(t, `structure.${x.structure}`, x.structure)}</span>
                {x.owner === 'business' && <span className="tag own">{t('templates.own')}</span>}
                <span className="tag muted">v{x.version}</span>
                {x.locale !== agentLanguage.split('-')[0] && <span className="tag warn">{t('templates.otherLanguage', { lang: x.locale.toUpperCase() })}</span>}
              </span>
            </button>
          ))}
          {templates && shown.length === 0 && <p className="muted">{t('templates.none')}</p>}
        </div>

        {picked && (
          <aside className="card ready-detail">
            <div className="row-between">
              <h2>{picked.icon} {picked.title}</h2>
              <button type="button" className="link" onClick={() => setPicked(undefined)}>✕</button>
            </div>
            <p className="muted">{picked.description}</p>
            {!preview && !error && <p className="muted">{t('ready.loading')}</p>}
            {preview && (
              <>
                <h4>{t('ready.goal')}</h4>
                <p>{String(seed['mission.goal'] ?? '')}</p>
                {list(seed.facts).length > 0 && (<><h4>{t('ready.collects')}</h4><ul>{list(seed.facts).map((f, i) => <li key={i}>{String(f.label)}</li>)}</ul></>)}
                {list(seed.routes).length > 0 && (<><h4>{t('ready.routes')}</h4><ul>{list(seed.routes).map((r, i) => <li key={i}>{String(r.topic)}</li>)}</ul></>)}
                {list(seed['statements.extra']).length > 0 && (<><h4>{t('ready.answers')}</h4><ul>{list(seed['statements.extra']).map((r, i) => <li key={i}>{String(r.text)}</li>)}</ul></>)}
                {Array.isArray(seed['boundaries.mustNotPromise']) && (<><h4>{t('ready.never')}</h4><ul>{(seed['boundaries.mustNotPromise'] as string[]).map((r, i) => <li key={i}>{r}</li>)}</ul></>)}
                {preview.filled.length > 0 && <p className="hint">{t('templates.filled', { list: preview.filled.map((k) => t(`templates.var.${k}`)).join(', ') })}</p>}

                <ReadOnlyNote t={t} need={PERM.agents} />
                {canCreate && (
                <div className="form-grid one ready-form">
                  <label>{t('ready.name')}<input maxLength={60} value={name} onChange={(e) => setName(e.target.value)} /></label>
                  {needsSource && (
                    <label>{t('ready.contactSource')}
                      <textarea rows={2} maxLength={500} value={contactSource} placeholder={t('ready.contactSource.placeholder')} onChange={(e) => setContactSource(e.target.value)} />
                      <span className="hint">{t('ready.contactSource.hint')}</span>
                    </label>
                  )}
                  {preview.unresolved.map((k) => (
                    <label key={k}>{t(`templates.var.${k}`)} <span className="muted small">({t('ready.optional')})</span>
                      <input maxLength={300} value={values[k] ?? ''} onChange={(e) => setValues({ ...values, [k]: e.target.value })} />
                    </label>
                  ))}
                  {otherMissing.length > 0 && <p className="banner warn small">{t('ready.incomplete')}</p>}
                  <button type="button" className="primary" disabled={busy || (needsSource && !contactSource.trim()) || otherMissing.length > 0} onClick={() => void create()}>
                    {busy ? t('ready.creating') : t('ready.create')}
                  </button>
                  <p className="hint">{t('ready.after')}</p>
                </div>
                )}
              </>
            )}
            {error && <div className="banner error small">{t(`error.${error}`) === `error.${error}` ? error : t(`error.${error}`)}</div>}
          </aside>
        )}
      </div>
    </section>
  );
};

/**
 * "Could not find the right agent?": the owner writes the need; the AI suggests matching ready agents or,
 * when none fits, the studio opens with that need as the first message (nothing is asked twice).
 */
const Finder = ({ api, t, agentLanguage, panelLanguage, onPick, onCustom, onClose }: {
  api: Api; t: T; agentLanguage: string; panelLanguage: string;
  onPick: (x: CatalogTemplate) => void; onCustom: (sessionId: string) => void; onClose: () => void;
}) => {
  const [need, setNeed] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<Array<{ needId?: string; reply: string; picks: Array<{ id: string; why: string; template: CatalogTemplate }> }>>([]);
  const [busy, setBusy] = useState<'match' | 'custom' | null>(null);
  // One finder talk = one AI session on the server.
  const [conversation] = useState(() => crypto.randomUUID().replaceAll('-', ''));
  const [error, setError] = useState<string>();

  const send = async () => {
    const msg = text.trim();
    if (!msg) return;
    const next = [...need, msg];
    setNeed(next);
    setText('');
    setBusy('match');
    setError(undefined);
    try {
      setTurns((x) => [...x, { reply: '', picks: [] }]);
      const r = await api.matchTemplates(next, agentLanguage, panelLanguage, conversation);
      setTurns((x) => [...x.slice(0, -1), r]);
    } catch (e) {
      setTurns((x) => x.slice(0, -1));
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(null);
    }
  };

  const custom = async () => {
    setBusy('custom');
    setError(undefined);
    try {
      const needId = turns.at(-1)?.needId;
      if (needId) void api.chooseNeed(needId, 'custom').catch(() => undefined);
      const v = await api.startStudio(undefined, panelLanguage);
      if (need.length) await api.sendStudio(v.sessionId, need.join('\n'));
      onCustom(v.sessionId);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
      setBusy(null);
    }
  };

  const last = turns.at(-1);
  return (
    <div className="card finder">
      <div className="row-between">
        <h2>{t('ready.finder.title')}</h2>
        <button type="button" className="link" onClick={onClose} aria-label={t('ready.finder.close')}>✕</button>
      </div>
      <div className="messages finder-messages">
        <div className="bubble assistant">{t('ready.finder.ask')}</div>
        {need.map((n, i) => (
          <div key={i}>
            <div className="bubble user">{n}</div>
            {turns[i] && (
              <div className="bubble assistant">
                {turns[i]!.reply || (busy === 'match' && i === need.length - 1 ? t('studio.thinking') : '')}
                {turns[i]!.picks.length > 0 && (
                  <div className="finder-picks">
                    {turns[i]!.picks.map((p) => (
                      <button key={p.id} type="button" className="template-card" onClick={() => {
                        const needId = turns[i]!.needId;
                        if (needId) void api.chooseNeed(needId, p.id).catch(() => undefined);
                        onPick(p.template);
                      }}>
                        <span className="template-icon" aria-hidden>{p.template.icon}</span>
                        <strong>{p.template.title}</strong>
                        <span className="hint">{p.why}</span>
                        <span className="finder-cta">{t('ready.finder.use')} →</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <form className="composer" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <textarea rows={2} value={text} placeholder={t('ready.finder.placeholder')} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
        <button type="submit" className="primary" disabled={busy !== null || !text.trim()}>{t('studio.send')}</button>
      </form>
      {need.length > 0 && last && busy !== 'match' && (
        <div className="finder-custom">
          <span className="muted small">{last.picks.length ? t('ready.finder.noneFit') : t('ready.finder.noMatch')}</span>
          <button type="button" className="secondary" disabled={busy !== null} onClick={() => void custom()}>{busy === 'custom' ? t('ready.creating') : t('ready.finder.custom')}</button>
        </div>
      )}
      {error && <div className="banner error small">{t(`error.${error}`) === `error.${error}` ? t('error.generic') : t(`error.${error}`)}</div>}
    </div>
  );
};
